import { agentById, type AgentId } from "./nav.js";

export type MessageKind = "text" | "card";

export type CardAction =
	| "start"
	| "accept_findings"
	| "open_connectors"
	| "open_channels";

export type ChatAttachment = {
	id: string;
	filename?: string;
	mediaType: string;
	/** data: or blob: URL — may be stripped when persisting threads. */
	url?: string;
};

/** Slim front-desk → specialist handoff shown on an assistant message. */
export type ChatHandoff = {
	handoffId: string;
	agentType: string;
	agentLabel: string;
	task: string;
	phase: "started" | "progress" | "done";
	/** Live activity line while working (“Updating src/pages/index.astro”). */
	note?: string;
	/** Latest screenshot the agent took while verifying its work. */
	screenshotUrl?: string;
	status?: "completed" | "pending" | "unavailable" | "failed";
	summary?: string;
	approvalId?: string;
	openAgentId?: string;
	/** Live site URL for the side preview canvas. */
	previewUrl?: string;
	/** Local decision after in-chat Approve / Reject. */
	decision?: "approved" | "rejected";
	decisionError?: string;
};

/** Fired after a successful in-chat gate decision. */
export type HandoffDecisionEvent = {
	handoffId: string;
	verdict: "approve" | "reject";
	handoff: ChatHandoff;
	patch: Partial<ChatHandoff>;
};

/** In-chat OAuth Connect card (Cursor-style). */
export type ChatConnect = {
	connectId: string;
	provider: string;
	label: string;
	phase: "awaiting" | "connected" | "timed_out";
	reason?: string;
	authorizeUrl?: string;
	portfolioCount?: number;
};

/** Mairko-hosted site clone progress (business-owner Clone your website). */
export type SitePublishPhase =
	| "pending_approval"
	| "publishing"
	| "deployed"
	| "failed";

export type SiteCloneWorkPhase =
	| "started"
	| "progress"
	| "ready"
	| "failed"
	| "queued"
	| "scraping"
	| "images"
	| "finalizing"
	| "rebuilding"
	| "building"
	| "uploading";

const SITE_CLONE_PHASES = new Set<string>([
	"started",
	"progress",
	"ready",
	"failed",
	"queued",
	"scraping",
	"images",
	"finalizing",
	"rebuilding",
	"building",
	"uploading",
]);

export function siteClonePhase(
	raw: unknown,
	fallback: SiteCloneWorkPhase = "progress",
): SiteCloneWorkPhase {
	if (typeof raw === "string" && SITE_CLONE_PHASES.has(raw)) {
		return raw as SiteCloneWorkPhase;
	}
	return fallback;
}

export type ChatSiteClone = {
	siteId: string;
	name: string;
	sourceUrl: string;
	phase: SiteCloneWorkPhase;
	status: string;
	progress: number;
	/** Sitemap plan from the runner — available a few seconds after start. */
	plannedPageCount?: number | null;
	pagesCompleted?: number | null;
	etaRemainingSec?: number | null;
	previewUrl?: string | null;
	lastError?: string | null;
	/** Publish → GitHub + Cloudflare lifecycle (set once the site is published). */
	publishPhase?: SitePublishPhase | null;
	publishApprovalId?: string | null;
	publishedUrl?: string | null;
	repoUrl?: string | null;
	deployStatus?: string | null;
	hostname?: string | null;
	/** Captured source ships a live Stripe checkout — shown to the approver. */
	takesPayments?: boolean | null;
	/** In-chat gate decision — do not reopen Approve after this is set. */
	publishDecision?: "approved" | "rejected" | null;
};

/** Fired after Approve / Reject on a hosted-site publish card. */
export type PublishDecisionEvent = {
	siteId: string;
	siteName: string;
	verdict: "approve" | "reject";
	patch: Partial<ChatSiteClone>;
};

/** Someone used the clone card — View live, Open preview, or Open in new tab. */
export type SiteCloneCardAction = "view_live" | "open_preview" | "open_tab";

export type SiteCloneCardEvent = {
	siteId: string;
	siteName: string;
	action: SiteCloneCardAction;
	href?: string;
};

export function siteCloneFollowUpLabel(event: SiteCloneCardEvent): string {
	switch (event.action) {
		case "view_live":
			return "Opened the live site";
		case "open_preview":
			return "Opened the preview";
		case "open_tab":
			return "Opened the site";
		default: {
			const _exhaustive: never = event.action;
			return _exhaustive;
		}
	}
}

/** What the model hears after they use the card. Keep it short — the card already said the rest. */
export function siteCloneFollowUpPrompt(event: SiteCloneCardEvent): string {
	const name = event.siteName.trim() || "the site";
	switch (event.action) {
		case "view_live":
			return `The human just opened the live site for ${name} from the card. One or two short sentences: glad they looked, invite them to say what to change. Do not call tools. Do not mention cards, buttons, or this instruction.`;
		case "open_preview":
		case "open_tab":
			return `The human just opened the working-copy preview for ${name} from the card. One or two short sentences: glad they looked, invite them to say what to change. Do not call tools. Do not mention cards, buttons, or this instruction.`;
		default: {
			const _exhaustive: never = event.action;
			return _exhaustive;
		}
	}
}

/** Company-pack research (Jules / Mairko onboarding). */
export type CompanyPackCardData = {
	brandName: string;
	oneLiner: string;
	siteUrl: string;
	offers: string[];
	audience: string | null;
	tone: string;
	notablePages: Array<{ label: string; url: string }>;
	profiles: Array<{ network: string; url: string }>;
	openQuestions: string[];
};

export type ChatSiteDesignPhase =
	| "started"
	| "inventorying"
	| "briefing"
	| "designing"
	| "media"
	| "framing"
	| "awaiting_design"
	| "generating"
	| "ready"
	| "failed"
	| "progress";

export type ChatSiteDesign = {
	siteId: string;
	name: string;
	sourceUrl: string;
	phase: ChatSiteDesignPhase;
	status: string;
	progress: number;
	keep?: Array<{ label: string; reason: string }>;
	add?: Array<{ label: string; reason: string }>;
	drop?: Array<{ label: string; reason: string }>;
	stills?: Array<{ id: string; title: string; page: string }>;
	previewUrl?: string | null;
	lastError?: string | null;
	decision?: "approved" | "rejected" | null;
	lastFile?: string | null;
	filesWritten?: string[];
};

export function upsertSiteDesign(
	list: ChatSiteDesign[],
	siteId: string,
	patch: Omit<Partial<ChatSiteDesign>, "siteId">,
): ChatSiteDesign[] {
	const idx = list.findIndex((row) => row.siteId === siteId);
	if (idx < 0) {
		const seeded: ChatSiteDesign = {
			siteId,
			name: "Your site",
			sourceUrl: "",
			phase: "progress",
			status: "cloning",
			progress: 5,
		};
		return [...list, { ...seeded, ...patch }];
	}
	return list.map((row, i) => (i === idx ? { ...row, ...patch } : row));
}

export function siteDesignInFlight(row: ChatSiteDesign): boolean {
	if (row.phase === "ready" || row.phase === "failed") return false;
	if (row.status === "preview_ready" || row.status === "failed") return false;
	return true;
}

export function mergeSiteDesignLists(
	current: ChatSiteDesign[] | undefined,
	incoming: ChatSiteDesign[],
): ChatSiteDesign[] | undefined {
	if (incoming.length === 0) return current;
	let next = current ?? [];
	for (const row of incoming) {
		next = upsertSiteDesign(next, row.siteId, row);
	}
	return next;
}

export type ChatCompanyResearch = {
	packId: string;
	siteUrl: string;
	phase: "started" | "progress" | "ready" | "failed";
	status: string;
	progress: number;
	brandName?: string;
	lastError?: string | null;
	card?: CompanyPackCardData | null;
};

export type ChatWorkingCopyAsk = {
	brandName: string;
	siteUrl?: string;
	decision?: "yes" | "later";
};

export type ChatMessage = {
	id: string;
	agentId: string;
	author: "agent" | "human" | "system";
	authorName: string;
	body: string;
	at: string;
	avatar?: string | null;
	/** Optional chain-of-thought / status shown via AI Elements Reasoning. */
	reasoning?: string;
	/** True while waiting on / streaming a model reply. */
	pending?: boolean;
	attachments?: ChatAttachment[];
	handoffs?: ChatHandoff[];
	connects?: ChatConnect[];
	siteClones?: ChatSiteClone[];
	/** Parallel redesign path — do not mix with siteClones. */
	siteDesigns?: ChatSiteDesign[];
	companyResearch?: ChatCompanyResearch[];
	/** Post-confirm invite — Yes opens checkout, Later waits. */
	workingCopyAsk?: ChatWorkingCopyAsk;
	card?: {
		title: string;
		body: string;
		primaryLabel?: string;
		secondaryLabel?: string;
		primaryAction?: CardAction;
		secondaryAction?: CardAction;
	};
};

export function collectSiteDesigns(messages: ChatMessage[]): ChatSiteDesign[] {
	const byId = new Map<string, ChatSiteDesign>();
	for (const message of messages) {
		for (const row of message.siteDesigns ?? []) {
			byId.set(row.siteId, row);
		}
	}
	return [...byId.values()];
}

/** True while a site clone is still scraping/building (sidebar spinner). */
export function siteCloneInFlight(clone: ChatSiteClone): boolean {
	if (clone.phase === "ready" || clone.phase === "failed") return false;
	if (clone.status === "preview_ready" || clone.status === "failed") {
		return false;
	}
	return true;
}

export function collectSiteClones(messages: ChatMessage[]): ChatSiteClone[] {
	const byId = new Map<string, ChatSiteClone>();
	for (const message of messages) {
		for (const clone of message.siteClones ?? []) {
			byId.set(clone.siteId, clone);
		}
	}
	return [...byId.values()];
}

export function upsertSiteClone(
	list: ChatSiteClone[],
	siteId: string,
	patch: Omit<Partial<ChatSiteClone>, "siteId">,
): ChatSiteClone[] {
	const idx = list.findIndex((row) => row.siteId === siteId);
	if (idx < 0) {
		const seeded: ChatSiteClone = {
			siteId,
			name: "Your site",
			sourceUrl: "",
			phase: "ready",
			status: "preview_ready",
			progress: 100,
		};
		return [...list, { ...seeded, ...patch }];
	}
	const existing = list[idx] as ChatSiteClone;
	const next = { ...existing, ...patch };
	if (
		existing.publishDecision &&
		patch.publishPhase === "pending_approval"
	) {
		const incomingId = patch.publishApprovalId?.trim() ?? "";
		const existingId = existing.publishApprovalId?.trim() ?? "";
		if (incomingId && incomingId !== existingId) {
			next.publishPhase = "pending_approval";
			next.publishDecision = null;
		} else {
			next.publishPhase =
				existing.publishPhase === "deployed"
					? "deployed"
					: existing.publishPhase === "failed"
						? "failed"
						: "publishing";
			next.publishDecision = existing.publishDecision;
		}
	}
	const copy = [...list];
	copy[idx] = next;
	return copy;
}

export function mergeSiteCloneLists(
	current: ChatSiteClone[] | undefined,
	incoming: ChatSiteClone[],
): ChatSiteClone[] | undefined {
	if (incoming.length === 0) return current;
	return incoming.reduce(
		(list, row) => upsertSiteClone(list, row.siteId, row),
		current ?? [],
	);
}

/** One clone card per site — later messages win so Approve isn’t reprinted. */
export function withLatestSiteCloneCards(
	messages: ChatMessage[],
): ChatMessage[] {
	const latest = new Map<string, string>();
	for (const message of messages) {
		for (const clone of message.siteClones ?? []) {
			latest.set(clone.siteId, message.id);
		}
	}
	return messages.map((message) => {
		if (!message.siteClones?.length) return message;
		const kept = message.siteClones.filter(
			(clone) => latest.get(clone.siteId) === message.id,
		);
		if (kept.length === message.siteClones.length) return message;
		return {
			...message,
			siteClones: kept.length > 0 ? kept : undefined,
		};
	});
}

export function collectInFlightSiteClones(
	messages: ChatMessage[],
): ChatSiteClone[] {
	return collectSiteClones(messages).filter(siteCloneInFlight);
}

export function companyResearchInFlight(row: ChatCompanyResearch): boolean {
	if (row.phase === "ready" || row.phase === "failed") return false;
	if (
		row.status === "ready" ||
		row.status === "failed" ||
		row.status === "confirmed"
	) {
		return false;
	}
	return true;
}

/**
 * Poll-narration leftover (“Working on it.”) that the model writes while
 * research_status loops. Hidden once the pack card is the reply.
 */
export function isStatusFillerBody(body: string): boolean {
	const text = body.trim();
	if (!text) return true;
	if (/research is underway|should be ready in just a moment/i.test(text)) {
		return true;
	}
	if (/existing context|welcome to mairko/i.test(text)) {
		return true;
	}
	if (text.length > 80) return false;
	return /^(working on it|on it|got it|hang tight|looking(\s+now)?|one (sec|second|moment)|give me a (sec|second|moment)|let me (look|check|see|take a look)|still (looking|checking|working)|checking now)[.!]?\s*$/i.test(
		text,
	);
}

/** Hide assistant prose when the pack card already is the message. */
export function hideAssistantBodyForCompanyResearch(
	rows: ChatCompanyResearch[] | undefined,
	_body: string,
): boolean {
	if (!rows?.length) return false;
	if (rows.some(companyResearchInFlight)) return true;
	return rows.some(
		(row) =>
			row.status === "ready" ||
			row.status === "confirmed" ||
			row.phase === "ready",
	);
}

/** A settled clone card is the message — don't narrate under it. */
export function hideAssistantBodyForSiteClone(
	clones: ChatSiteClone[] | undefined,
): boolean {
	if (!clones?.length) return false;
	return clones.some((clone) => !siteCloneInFlight(clone));
}

export function isGateChipBody(body: string): boolean {
	return /^(Approved|Rejected)$/i.test(body.trim());
}

/** Publish/clone leftover the card already shows (“deploying now…”). */
export function isCloneStatusNarration(body: string): boolean {
	const text = body.trim();
	if (!text) return false;
	return /your site is deploying|i('?ll| will) let you know when it'?s live|going live now|site is (now )?live|published (and|&) live|i('?ll| will) ping you when/i.test(
		text,
	);
}

/** A leftover status bubble with no card — drop it from the thread. */
export function isOrphanStatusFiller(message: ChatMessage): boolean {
	if (message.author !== "agent") return false;
	if (message.workingCopyAsk) return false;
	if (message.companyResearch?.length) return false;
	if (message.siteClones?.length) return false;
	if (message.siteDesigns?.length) return false;
	if (message.handoffs?.length) return false;
	if (message.connects?.length) return false;
	if (message.card) return false;
	if (message.attachments?.length) return false;
	return isStatusFillerBody(message.body) || isCloneStatusNarration(message.body);
}

/** Skip card narration when building model history — the card is the fact. */
export function chatHistoryBody(message: ChatMessage): string | null {
	if (hideAssistantBodyForSiteClone(message.siteClones)) return null;
	if (isCloneStatusNarration(message.body)) return null;
	if (
		message.author === "agent" &&
		isStatusFillerBody(message.body) &&
		!message.workingCopyAsk
	) {
		return null;
	}
	const text = message.body.trim();
	return text.length > 0 ? text : null;
}

/**
 * After a clone card, the card owns the floor. Hide later assistant
 * chatter until they use the card or type something (Approved/Rejected
 * chips do not count — those are gate chrome, not a real turn).
 */
export function withHeldAfterSiteClone(
	messages: ChatMessage[],
): ChatMessage[] {
	let holding = false;
	const out: ChatMessage[] = [];
	for (const message of messages) {
		if (hideAssistantBodyForSiteClone(message.siteClones)) {
			holding = true;
			out.push(message);
			continue;
		}
		if (!holding) {
			out.push(message);
			continue;
		}
		if (message.author === "human") {
			if (isGateChipBody(message.body)) {
				out.push(message);
				continue;
			}
			holding = false;
			out.push(message);
			continue;
		}
		if (
			message.companyResearch?.length ||
			message.handoffs?.length ||
			message.connects?.length ||
			message.workingCopyAsk ||
			message.card
		) {
			out.push(message);
			continue;
		}
	}
	return out;
}

export function stripWorkingCopyHedge(body: string): string {
	return body.replace(/\s*You can say no\.?\s*$/i, "").trim();
}

export function looksLikeWorkingCopyInvite(body: string): boolean {
	return /want a working copy|ready for a working copy|ready for us to design|design (you|[\w'’ ]{1,40}) a new site/i.test(
		body,
	);
}

/** UI / SitesHub publish line — not a model turn. */
export function parseHostedPublishCommand(
	text: string,
): { siteId: string; siteName: string } | null {
	const match = text
		.trim()
		.match(
			/^Publish the hosted site "([^"]+)" \(siteId ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)\.?$/i,
		);
	if (!match?.[1] || !match[2]) return null;
	return { siteName: match[1], siteId: match[2] };
}

export function brandFromThread(
	messages: ChatMessage[],
): { brandName: string; siteUrl?: string } | null {
	for (let i = messages.length - 1; i >= 0; i--) {
		const rows = messages[i]?.companyResearch;
		if (!rows?.length) continue;
		for (const row of [...rows].reverse()) {
			const brandName = row.card?.brandName ?? row.brandName;
			if (!brandName) continue;
			return {
				brandName,
				siteUrl: row.card?.siteUrl ?? row.siteUrl,
			};
		}
	}
	return null;
}

/** Attach Yes/Later to an existing invite so older threads get the buttons. */
export function withWorkingCopyAsk(
	message: ChatMessage,
	brand: { brandName: string; siteUrl?: string } | null,
): ChatMessage {
	const body = stripWorkingCopyHedge(message.body);
	if (message.workingCopyAsk) {
		const siteUrl =
			message.workingCopyAsk.siteUrl?.trim() || brand?.siteUrl;
		const nextAsk =
			siteUrl && siteUrl !== message.workingCopyAsk.siteUrl
				? { ...message.workingCopyAsk, siteUrl }
				: message.workingCopyAsk;
		if (body === message.body && nextAsk === message.workingCopyAsk) {
			return message;
		}
		return { ...message, body, workingCopyAsk: nextAsk };
	}
	if (message.author !== "agent" || !brand) return message;
	if (!looksLikeWorkingCopyInvite(body)) return message;
	return {
		...message,
		body,
		workingCopyAsk: {
			brandName: brand.brandName,
			siteUrl: brand.siteUrl,
		},
	};
}

/** True while the model stream is open — blocks the composer. */
export function chatMessagesBusy(messages: ChatMessage[]): boolean {
	const now = Date.now();
	for (const message of messages) {
		if (!message.pending) continue;
		// Connect wait is a human CTA — don't soft-lock typing for the full OAuth
		// poll window (and a misconfigured provider used to freeze chat entirely).
		if (
			message.connects?.some(
				(c) => c.phase === "awaiting" || c.phase === "timed_out",
			)
		) {
			continue;
		}
		const started = Date.parse(message.at);
		if (Number.isFinite(started) && now - started > 8_000) continue;
		return true;
	}
	return false;
}

/**
 * Sidebar / recents spinner: pending reply OR background clone/research.
 * Clones must not lock the composer — capture can outlive the agent turn, and a
 * stuck card would soft-deadlock chat forever.
 */
export function chatHasBackgroundWork(messages: ChatMessage[]): boolean {
	for (const message of messages) {
		if (message.pending) return true;
		if (message.siteClones?.some(siteCloneInFlight)) return true;
		if (message.siteDesigns?.some(siteDesignInFlight)) return true;
		if (message.companyResearch?.some(companyResearchInFlight)) return true;
	}
	return false;
}

/** Seed timeline for non-onboarding agents until DOs stream for real. */
export function seedMessages(agentId: string): ChatMessage[] {
	const now = Date.now();
	if (agentId === "onboarding") {
		// Onboarding timeline is owned by onboarding-flow.ts
		return [];
	}

	const agent = agentById(agentId as AgentId);

	const body = (() => {
		switch (agent.id) {
			case "paid_media":
				return "I run after onboarding has a company pack and Google Ads is connected. Builds stay PAUSED until you approve enable.";
			case "ga4_anomaly":
				return "Connect GA4 — I’ll watch every property in your portfolio for traffic anomalies (read-only).";
			case "seo":
				return "I’m Theo — organic search. Search Console unlocks me for real; until then I can walk you through how I’d prioritize.";
			case "content":
				return "I’m Iris — briefs and drafts in your voice. Publish always waits on your approval.";
			case "social":
				return "I’m Sol — channel-native posts and calendars. Nothing goes live without your say.";
			case "email":
				return "I’m Vera — journeys and campaigns. Sends stay staged until you approve.";
			case "creative":
				return "I’m Kai — creative briefs and variants. Assets are proposals until you approve them.";
			case "cro":
				return "I’m Quinn — hypotheses and landing tests. Variants only ship after approval.";
			case "local":
				return "I’m Dana — Google Business Profile and nearby demand. Listings first, then the rest.";
			case "developer":
				return "I’m Rhea — sites, DNS, and deploys. Inventory first; production waits on your approval.";
			case "reporting":
				return "I’m Arlo — digests that say what moved and what to do next. Recommendations wait on you.";
			case "onboarding":
				return "";
			default: {
				const _exhaustive: never = agent.id;
				return _exhaustive;
			}
		}
	})();

	return [
		{
			id: `seed-${agent.id}-1`,
			agentId: agent.id,
			author: "agent",
			authorName: agent.name,
			avatar: agent.avatar,
			body,
			at: new Date(now - 10_000).toISOString(),
		},
	];
}
